/**
 * sirus/ActionBarExtras — R232q20 Phase 2 (опциональные доп-панели).
 *
 * Воспроизводит четыре дополнительные панели из WoW 3.3.5a "Параметры
 * интерфейса → Панели команд":
 *   • Right Bar 1 — вертикаль у правого края экрана      (bar2).
 *   • Right Bar 2 — вертикаль чуть левее Right Bar 1     (bar3).
 *   • Bottom Left — горизонталь под основным хотбаром    (bar4).
 *   • Bottom Right — горизонталь под Bottom Left          (bar5).
 *
 * Каждая панель — 12 слотов 36×36 с зазором 6px. Видимость отдельно
 * управляется флагами `useUiSettings()`:
 *   showRightBar1 / showRightBar2 / showBottomLeft / showBottomRight.
 *   Дефолтно все четыре выключены — соответствует свежему клиенту 3.3.5a.
 *
 * State sources:
 *   useUiSettings — флаги видимости.
 *   useHotbar     — bindings[classId] (та же строка, что и у основной
 *                    панели, см. блок DEFERRED ниже).
 *   useKeybinds   — переопределение лейбла клавиш per-bar
 *                    (`hotbar.bar2/3/4.<n>`; bar5 пока без префикса).
 *   useAbilityCD  — кулдаун + GCD пульс на иконке.
 *   useUI         — `stage` гейт: рендерим только во `world`.
 *   useNet        — self.resource → синяя OOM-маска на иконке.
 *
 * Декомпозиция: вместо дублирования визуала из ActionBar.tsx все 4
 * панели делят общий `<HotbarSlot bar="…" idx={n} />`. Он сам подписан
 * на нужные срезы стора и сам ловит drag-drop из SpellBook (mime
 * `HOTBAR_DND_MIME`) — родителю остаётся только разложить 12 слотов в
 * row/column.
 *
 * z-index = 30 (как у основной ActionBar).
 *
 * Mobile (`@media (pointer: coarse)`): из-за бюджета по экрану на
 * мобиле все четыре extras скрыты по умолчанию — пользователь явно
 * включает их в настройках. Если флаг всё-таки выставлен, вертикальные
 * панели становятся горизонтальной полосой со скроллом, а нижние —
 * слегка ужимаются (consistent with `data-mobile-shrink` подходом
 * основной панели).
 *
 * DEFERRED (родитель допилит позже):
 *   • Per-bar page switcher (1..6). hotbar-store пока хранит ровно одну
 *     строку на класс (HOTBAR_SIZE = 12), поэтому все extras читают тот
 *     же `bindings[classId]`, что и main bar1. Когда стор получит
 *     multi-row (по barName и/или page), `HotbarSlot` уже принимает
 *     `bar` пропсом и сможет читать соответствующую строку без правок
 *     в этом файле.
 *   • Реальный send-cast в сеть. Сейчас, как и в ActionBar.tsx, клик
 *     стартует локальный CD/GCD и пишет debug-лог; World.tsx подключит
 *     реальный network-bridge через тот же глобальный hook.
 *   • Range-tint красным. Хук на targetId зарезервирован.
 */

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';

import { abilityById, type Ability, type ClassId } from '../../data/classes.js';
import {
  HOTBAR_DND_MIME,
  HOTBAR_SIZE,
  useHotbar,
} from '../../state/hotbar-store.js';
import { useKeybinds } from '../../state/keybinds-store.js';
import { useAbilityCD } from '../../state/ability-cd-store.js';
import { useUI } from '../../state/store.js';
import { useNet, useSelfPlayer } from '../../state/net-store.js';
import { useUiSettings } from '../../state/ui-settings-store.js';

/** Имена баров — те же, что у keybinds-store (`hotbar.<bar>.<n>`). */
type BarName = 'bar2' | 'bar3' | 'bar4' | 'bar5';

const SLOT_PX = 36;
const SLOT_GAP = 6;

/** Дефолтные лейблы клавиш (соответствует 1..0,-,= с модификатором). */
const SLOT_LABELS: readonly string[] = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '=',
];

/** Префикс модификатора для подписи под слотом — Sirus / WoW WotLK 3.3.5a. */
const BAR_LABEL_PREFIX: Record<BarName, string> = {
  bar2: 'S+', // Shift+
  bar3: 'C+', // Ctrl+
  bar4: 'A+', // Alt+
  bar5: '',   // bar5 без префикса (WotLK 3.3.5a "BottomRight" не имеет default-кейбинда)
};

type DragPayload = { abilityId?: string; fromSlot?: number };

/* ─────────────────────────────────────────────────────────────────
 * ActionBarExtras — корневой компонент.
 * ──────────────────────────────────────────────────────────────── */

export function ActionBarExtras() {
  // Локальный 100ms тик нужен для пере-отрисовки кулдаун-свипа и
  // GCD-пульса (сами стор-апдейты не дёргают рендер по таймеру).
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => (t + 1) & 0xffff), 100);
    return () => window.clearInterval(id);
  }, []);

  const stage = useUI((s) => s.stage);
  const showRightBar1 = useUiSettings((s) => s.showRightBar1);
  const showRightBar2 = useUiSettings((s) => s.showRightBar2);
  const showBottomLeft = useUiSettings((s) => s.showBottomLeft);
  const showBottomRight = useUiSettings((s) => s.showBottomRight);
  const forceMobileUi = useUiSettings((s) => s.forceMobileUi);

  if (stage !== 'world') return null;

  const slotIndices = Array.from({ length: HOTBAR_SIZE }, (_, i) => i);

  const verticalStyle = (right: number): CSSProperties => ({
    position: 'fixed',
    right,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 30,
    display: 'flex',
    flexDirection: 'column',
    gap: SLOT_GAP,
    pointerEvents: 'auto',
    fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
    color: 'var(--aiwari-fg, #E8E1DA)',
  });

  const horizontalStyle = (bottom: number): CSSProperties => ({
    position: 'fixed',
    bottom,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 30,
    display: 'flex',
    flexDirection: 'row',
    gap: SLOT_GAP,
    pointerEvents: 'auto',
    fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
    color: 'var(--aiwari-fg, #E8E1DA)',
  });

  return (
    <>
      {showRightBar1 && (
        <div
          className="aiwari-action-bar-extras aiwari-action-bar-extras--right1"
          style={verticalStyle(4)}
          data-extras-bar="bar2"
          data-orientation="vertical"
          data-mobile-shrink="true"
          aria-label="Дополнительная панель команд 1 (правая)"
        >
          {slotIndices.map((i) => (
            <HotbarSlot key={`bar2-${i}`} bar="bar2" idx={i} />
          ))}
        </div>
      )}

      {showRightBar2 && (
        <div
          className="aiwari-action-bar-extras aiwari-action-bar-extras--right2"
          style={verticalStyle(56)}
          data-extras-bar="bar3"
          data-orientation="vertical"
          data-mobile-shrink="true"
          aria-label="Дополнительная панель команд 2 (правая, внутренняя)"
        >
          {slotIndices.map((i) => (
            <HotbarSlot key={`bar3-${i}`} bar="bar3" idx={i} />
          ))}
        </div>
      )}

      {showBottomLeft && (
        <div
          className="aiwari-action-bar-extras aiwari-action-bar-extras--bottomleft"
          style={horizontalStyle(60)}
          data-extras-bar="bar4"
          data-orientation="horizontal"
          data-mobile-shrink="true"
          aria-label="Дополнительная панель команд 3 (нижняя левая)"
        >
          {slotIndices.map((i) => (
            <HotbarSlot key={`bar4-${i}`} bar="bar4" idx={i} />
          ))}
        </div>
      )}

      {showBottomRight && (
        <div
          className="aiwari-action-bar-extras aiwari-action-bar-extras--bottomright"
          style={horizontalStyle(22)}
          data-extras-bar="bar5"
          data-orientation="horizontal"
          data-mobile-shrink="true"
          aria-label="Дополнительная панель команд 4 (нижняя правая)"
        >
          {slotIndices.map((i) => (
            <HotbarSlot key={`bar5-${i}`} bar="bar5" idx={i} />
          ))}
        </div>
      )}

      {/* Mobile: вертикальные панели → скролл-полоса, ужимка x0.85.
          Если юзер форсит мобилу через uiSettings.forceMobileUi —
          применяем тот же стиль на любом pointer'е. */}
      <style>{`
        @media (pointer: coarse) {
          .aiwari-action-bar-extras[data-mobile-shrink='true'][data-orientation='horizontal'] {
            transform: translateX(-50%) scale(0.85);
            transform-origin: bottom center;
          }
          .aiwari-action-bar-extras[data-mobile-shrink='true'][data-orientation='vertical'] {
            top: auto;
            bottom: 110px;
            right: 4px;
            transform: none;
            flex-direction: row;
            max-width: calc(100vw - 16px);
            overflow-x: auto;
            scrollbar-width: none;
          }
          .aiwari-action-bar-extras[data-mobile-shrink='true'][data-orientation='vertical']::-webkit-scrollbar {
            display: none;
          }
        }
        ${forceMobileUi ? `
          .aiwari-action-bar-extras[data-orientation='horizontal'] {
            transform: translateX(-50%) scale(0.85);
            transform-origin: bottom center;
          }
          .aiwari-action-bar-extras[data-orientation='vertical'] {
            top: auto;
            bottom: 110px;
            right: 4px;
            transform: none;
            flex-direction: row;
            max-width: calc(100vw - 16px);
            overflow-x: auto;
          }
        ` : ''}
      `}</style>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * HotbarSlot — общий компонент слота.
 *
 * Используется всеми четырьмя extras и (потенциально) основной
 * ActionBar в будущем рефакторе. Сам подписан на нужные срезы стора —
 * родитель только указывает barName + idx.
 * ──────────────────────────────────────────────────────────────── */

function HotbarSlotImpl({ bar, idx }: { bar: BarName; idx: number }) {
  // R232q86zdb (Wave 2 #1): narrow self selector — avoid subscribing to the
  // whole `players` map (would re-render every HotbarSlot, 4×12 = 48 of
  // them, on every remote player movement patch).
  const self = useSelfPlayer();
  const bindings = useHotbar((s) => s.bindings);
  const ensureRow = useHotbar((s) => s.ensureRow);
  const bind = useHotbar((s) => s.bind);
  const clear = useHotbar((s) => s.clear);
  const swap = useHotbar((s) => s.swap);
  const resolveKey = useKeybinds((s) => s.resolveKey);
  // Подписка на keybinds — чтобы перепривязка в Settings live-refresh-ила лейбл.
  const keybinds = useKeybinds((s) => s.bindings);
  void keybinds;

  const classId = (self?.classId ?? null) as ClassId | null;
  const resource = self?.resource ?? 0;

  // R232q20: hotbar-store хранит одну строку на класс. Все extras пока
  // делят её с bar1. Когда сторe появится multi-row by barName/page —
  // тут поменяется источник; сигнатура HotbarSlot не меняется.
  useEffect(() => {
    if (!classId) return;
    ensureRow(classId);
  }, [classId, ensureRow]);

  const row = useMemo(() => {
    if (!classId) return new Array<string | null>(HOTBAR_SIZE).fill(null);
    return bindings[classId] ?? new Array<string | null>(HOTBAR_SIZE).fill(null);
  }, [classId, bindings]);

  const ability: Ability | null = classId && row[idx]
    ? abilityById(classId, row[idx] as string) ?? null
    : null;

  /* ─── click: оптимистичный CD + GCD, фактический send делает World.tsx ─── */

  const onClick = useCallback(() => {
    if (!classId || !ability) return;
    const cd = useAbilityCD.getState();
    if (cd.remaining(ability.id) > 0) return;
    if (cd.gcdRemaining() > 0) return;
    cd.startCooldown(ability.id, ability.cooldown * 1000);
    cd.triggerGcd((self as { hastePct?: number } | null)?.hastePct ?? 0);
    if (ability.windup > 0) {
      cd.startCast(ability.id, ability.name.ru, ability.windup * 1000);
      window.setTimeout(() => useAbilityCD.getState().clearCast(), ability.windup * 1000);
    } else {
      cd.pulseInstant(ability.id, ability.name.ru);
    }
    const w = window as unknown as { __aiwariCastSlot?: (slot: number, id: string) => void };
    if (typeof w.__aiwariCastSlot === 'function') {
      try { w.__aiwariCastSlot(idx, ability.id); } catch { /* noop */ }
    } else {
      // eslint-disable-next-line no-console
      console.debug('[sirus/ActionBarExtras] castSlot', bar, idx, ability.id);
    }
  }, [classId, ability, self, idx, bar]);

  /* ─── рендер ─── */

  const cd = useAbilityCD.getState();
  const remaining = ability ? cd.remaining(ability.id) : 0;
  const onCd = remaining > 0;
  const cdSec = onCd ? Math.ceil(remaining / 100) / 10 : 0;
  const cdSweepDeg = ability && onCd && ability.cooldown > 0
    ? Math.min(360, (remaining / (ability.cooldown * 1000)) * 360)
    : 0;
  const gcdRem = cd.gcdRemaining();
  const gcdActive = gcdRem > 0;
  const gcdSweepDeg = gcdActive ? Math.min(360, (gcdRem / 1500) * 360) : 0;
  const oom = ability ? resource < ability.cost : false;

  // Лейбл клавиши — kee-bindable per-bar (bar5 без default-биндинга).
  const rawLabel = (bar === 'bar5'
    ? '' // нет default-биндинга
    : resolveKey(`hotbar.${bar}.${idx + 1}`) || '') || '';
  const stripped = rawLabel ? rawLabel.replace(/^.*\+/, '') : SLOT_LABELS[idx];
  const keyLabel = `${BAR_LABEL_PREFIX[bar]}${stripped}`;

  const slotStyle: CSSProperties = {
    position: 'relative',
    width: SLOT_PX,
    height: SLOT_PX,
    padding: 0,
    background: 'var(--aiwari-frame-stone, #1F1A14)',
    borderRadius: 2,
    boxSizing: 'border-box',
    cursor: ability ? 'pointer' : 'default',
    userSelect: 'none',
  };

  const handleDragStart = (e: React.DragEvent<HTMLButtonElement>) => {
    if (!ability) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(
      HOTBAR_DND_MIME,
      JSON.stringify({ abilityId: ability.id, fromSlot: idx } satisfies DragPayload),
    );
  };

  const handleDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
    if (e.dataTransfer.types.includes(HOTBAR_DND_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!classId) return;
    const raw = e.dataTransfer.getData(HOTBAR_DND_MIME);
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as DragPayload;
      if (!payload || typeof payload !== 'object') return;
      const fromSlot = typeof payload.fromSlot === 'number' ? payload.fromSlot : -1;
      if (!payload.abilityId) return;
      if (fromSlot >= 0 && fromSlot < HOTBAR_SIZE) {
        swap(classId, fromSlot, idx);
      } else {
        bind(classId, idx, payload.abilityId);
      }
    } catch { /* malformed — ignore */ }
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (classId) clear(classId, idx);
  };

  const tooltip = ability
    ? `${ability.name.ru} — ${ability.desc.ru}\nПКМ — снять с панели`
    : 'Перетащи заклинание из книги (P)';

  return (
    <button
      type="button"
      className="aiwari-icon-slot"
      style={slotStyle}
      title={tooltip}
      draggable={!!ability}
      onClick={onClick}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onContextMenu={handleContextMenu}
      data-bar={bar}
      data-slot={idx}
      data-disabled={!ability ? 'true' : undefined}
    >
      {ability ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22,
            lineHeight: 1,
            filter: onCd ? 'grayscale(0.85) brightness(0.55)' : undefined,
          }}
        >
          {ability.icon}
        </span>
      ) : (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            color: 'var(--aiwari-fg-dim, hsl(34 14% 35%))',
            fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
            pointerEvents: 'none',
          }}
        >◇</span>
      )}

      {/* Кулдаун-свип. */}
      {onCd && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: `conic-gradient(rgba(0,0,0,0.62) 0deg ${cdSweepDeg}deg, transparent ${cdSweepDeg}deg 360deg)`,
            pointerEvents: 'none',
            transition: 'background 80ms linear',
          }}
        />
      )}
      {/* Цифра кулдауна. */}
      {onCd && remaining >= 1000 && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--aiwari-gold-soft, #FFD479)',
            fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
            fontSize: 13,
            fontWeight: 700,
            textShadow: '0 1px 2px #000',
            pointerEvents: 'none',
          }}
        >
          {cdSec.toFixed(cdSec >= 10 ? 0 : 1)}
        </span>
      )}
      {/* GCD пульс. */}
      {gcdActive && !onCd && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: `conic-gradient(rgba(255,255,255,0.16) 0deg ${gcdSweepDeg}deg, transparent ${gcdSweepDeg}deg 360deg)`,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* OOM-маска. */}
      {oom && !onCd && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(40, 90, 200, 0.28)',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Лейбл клавиши. */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          right: 2,
          bottom: 1,
          fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
          fontSize: 9,
          lineHeight: 1,
          color: 'var(--aiwari-fg-muted, #B19B7E)',
          textShadow: '0 1px 0 #000',
          pointerEvents: 'none',
        }}
      >{keyLabel}</span>
    </button>
  );
}

/**
 * R232q86zdb (Wave 2 #1): React.memo wrapper for HotbarSlot.
 *
 * 48 instances of this exist when all 4 extras are visible (4 bars × 12
 * slots). With the narrow `useSelfPlayer()` subscription above, each
 * instance only re-renders when self's class / resource / hp /
 * hastePct actually changes — NOT on every remote player position patch.
 * The memo additionally short-circuits when the parent re-renders with
 * the same `bar` / `idx` props (the common case).
 */
export const HotbarSlot = memo(
  HotbarSlotImpl,
  (a, b) => a.bar === b.bar && a.idx === b.idx,
);

export default ActionBarExtras;
